import React, { useMemo } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { CAPTION_FALLBACK_SEC } from '../preview/previewClock';
import type { LaneProps } from './Timeline';
import { spanBox } from './timelineGeometry';
import styles from './LyricsLane.module.scss';

/**
 * The lyrics lane (design Timeline): the timed lines of the song. It is read only here: the timing
 * belongs to the song (the Lyrics tool links to its editor). A click selects the line and moves
 * the play head to it.
 */
const LyricsLaneComponent: React.FC<LaneProps> = ({ view }) => {
  const { data, selection, select, transport } = useMusicVideoEditor();
  const lines = useMemo(() => {
    const timed = data.lyrics.lines.filter((line) => line.startMs !== null);
    return timed.map((line, position) => {
      const startSec = (line.startMs ?? 0) / 1000;
      const nextStart = timed[position + 1]?.startMs ?? null;
      const endSec = (line.endMs ?? nextStart ?? (line.startMs ?? 0) + CAPTION_FALLBACK_SEC * 1000) / 1000;
      return { index: line.index, text: line.text, startSec, endSec };
    });
  }, [data.lyrics.lines]);
  const selectedIndex = selection?.kind === 'lyric' ? selection.index : null;

  return (
    <div className={clsx(styles.lane, styles.hLyrics)}>
      {lines.map((line) => {
        const box = spanBox(line.startSec, line.endSec, view);
        if (box === null) {
          return null;
        }
        return (
          <button
            key={line.index}
            type="button"
            className={clsx(styles.textBlock, styles.lyricBlock, line.index === selectedIndex && styles.blockOn)}
            style={{ left: `${box.left}%`, width: `calc(${box.width}% - 2px)` }}
            aria-pressed={line.index === selectedIndex}
            onClick={() => {
              select({ kind: 'lyric', index: line.index });
              transport.seek(line.startSec);
            }}
          >
            {line.text}
          </button>
        );
      })}
    </div>
  );
};

export const LyricsLane = TypedMemo(LyricsLaneComponent);
